import { Box, Button, Skeleton, styled, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getPromptsIncrementally } from '../../redux/actions/promptActions';
import { selectPrompts, setSearchQuery } from '../../redux/reducers/promptReducer';
import { AppDispatch } from '../../redux/store';
import { FiltersDisplayMode, PromptLocation } from '../../Types/enums';
import { Prompt } from '../../Types/prompt';
import DebouncedSearchInput from '../DebouncedSearchInput';
import PromptCard from './PromptCard';
import PromptFilters from './PromptFilters';

const LoadMoreButton = styled(Button)({
  margin: '4px 0px 8px 0px',
  color: 'black',
  fontWeight: 'bold',
});

const PromptList = ({ onCardClick }: { onCardClick?: (prompt: Prompt) => void }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { prompts, searchQuery, page, totalPages } = useSelector(selectPrompts);

  const onLoadMore = useCallback(() => {
    dispatch(getPromptsIncrementally({ pageAction: 'increment' }));
  }, [dispatch]);

  return !prompts ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 8,
        gap: 2,
      }}
    >
      <Skeleton variant="rectangular" width="100%" height={24} />
      <Skeleton variant="rectangular" width="100%" height={64} />
      <Skeleton variant="rectangular" width="100%" height={56} />
      <Skeleton variant="rectangular" width="100%" height={124} />
      <Skeleton variant="rectangular" width="100%" height={124} />
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1,
        pb: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          gap: 2,
          width: '100%',
        }}
      >
        <Typography variant="body2" sx={{ marginLeft: 1, fontWeight: '700' }}>
          Select one of the following options:
        </Typography>
        <PromptFilters displayMode={FiltersDisplayMode.DROPDOWN} />
        <DebouncedSearchInput
          value={searchQuery}
          valueUpdated={(value) => {
            dispatch(setSearchQuery(value));
          }}
          labelText="Search Library"
        />
        {prompts.map((prompt: Prompt) => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            location={PromptLocation.PROMPT_LIST}
            onClick={onCardClick}
          />
        ))}
        {prompts.length === 0 && <Typography align="center">No prompts found</Typography>}
        {page < totalPages && (
          <LoadMoreButton onClick={onLoadMore}>
            <Typography variant="caption2">+ Load More</Typography>
          </LoadMoreButton>
        )}
      </Box>
    </Box>
  );
};

export default PromptList;
